import React, { useState, useEffect } from 'react';
import { Form, Input, Button, notification, Typography, Divider } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { LockOutlined, ArrowRightOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../api/axios';
import AuthLayout from '../layouts/AuthLayout';

const { Title, Text } = Typography;

const ResetPassword = () => {
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    useEffect(() => {
        if (!token || !email) {
            setInvalid(true);
        }
    }, [token, email]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const response = await api.post('/reset-password', {
                email,
                token,
                password: values.password,
                password_confirmation: values.password_confirmation,
            });

            if (response.data.status === 'success') {
                setDone(true);
            } else {
                notification.error({ message: 'Reset Failed', description: response.data.message || 'Please try again.' });
            }
        } catch (error) {
            if (error.response?.status === 400) {
                notification.error({ message: 'Link Expired', description: 'This reset link is invalid or has expired. Please request a new one.' });
                setInvalid(true);
            } else if (error.response?.status === 422) {
                const errors = error.response.data.errors || {};
                Object.values(errors).flat().forEach((msg) =>
                    notification.error({ message: 'Validation Error', description: msg })
                );
            } else {
                notification.error({ message: 'Reset Failed', description: 'Something went wrong. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Reset Password"
                description="Set a new password for your Oottan account."
                canonical="/reset-password"
                noindex={true}
            />
            <AuthLayout>
                <div style={{ width: '100%' }}>
                    <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                        <div style={{
                            width: '60px', height: '60px', borderRadius: '16px',
                            background: done
                                ? 'linear-gradient(135deg,#16A34A,#22C55E)'
                                : invalid
                                    ? 'linear-gradient(135deg,#DC2626,#EF4444)'
                                    : 'linear-gradient(135deg,#AD1457,#D81B60)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px',
                            boxShadow: done ? '0 8px 24px rgba(22,163,74,0.3)' : '0 8px 24px rgba(173,20,87,0.3)',
                        }}>
                            {done
                                ? <CheckCircleOutlined style={{ fontSize: '1.6rem', color: 'white' }} />
                                : <LockOutlined style={{ fontSize: '1.6rem', color: 'white' }} />
                            }
                        </div>
                        <Title level={2} style={{ color: '#1A1A2E', marginBottom: '6px', fontWeight: '800', fontSize: '1.8rem' }}>
                            {done ? 'Password Reset!' : invalid ? 'Link Expired' : 'Reset Password'}
                        </Title>
                        <Text style={{ color: '#64748B', fontSize: '0.95rem' }}>
                            {done
                                ? 'Your password has been changed successfully.'
                                : invalid
                                    ? 'This reset link is invalid or has expired.'
                                    : 'Enter your new password below'}
                        </Text>
                    </div>

                    {done ? (
                        <div>
                            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', textAlign: 'center' }}>
                                <Text style={{ color: '#166534', fontSize: '0.9rem' }}>
                                    You can now sign in with your new password.
                                </Text>
                            </div>
                            <Button type="primary" block size="large" icon={<ArrowRightOutlined />} onClick={() => navigate('/login')} style={{ height: '50px', borderRadius: '10px', fontSize: '1rem', fontWeight: '700', background: 'linear-gradient(135deg,#AD1457,#D81B60)', border: 'none', boxShadow: '0 6px 20px rgba(173,20,87,0.35)' }}>
                                Go to Login
                            </Button>
                        </div>
                    ) : invalid ? (
                        <div>
                            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', textAlign: 'center' }}>
                                <Text style={{ color: '#991B1B', fontSize: '0.9rem' }}>
                                    Reset links expire after 1 hour and can only be used once. Please request a new one.
                                </Text>
                            </div>
                            <Button type="primary" block size="large" onClick={() => navigate('/forgot-password')} style={{ height: '50px', borderRadius: '10px', fontSize: '1rem', fontWeight: '700', background: 'linear-gradient(135deg,#AD1457,#D81B60)', border: 'none', boxShadow: '0 6px 20px rgba(173,20,87,0.35)' }}>
                                Request New Link
                            </Button>
                        </div>
                    ) : (
                        <Form name="reset-password" onFinish={handleSubmit} layout="vertical" size="large">
                            <Form.Item name="password" rules={[{ required: true, message: 'Please enter your new password' }, { min: 8, message: 'Password must be at least 8 characters' }]}>
                                <Input.Password prefix={<LockOutlined style={{ color: '#AD1457' }} />} placeholder="New password (min. 8 characters)" style={{ borderRadius: '10px', height: '50px' }} />
                            </Form.Item>

                            <Form.Item
                                name="password_confirmation"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Please confirm your new password' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Passwords do not match'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined style={{ color: '#AD1457' }} />} placeholder="Confirm new password" style={{ borderRadius: '10px', height: '50px' }} />
                            </Form.Item>

                            <Form.Item style={{ marginBottom: '12px' }}>
                                <Button type="primary" htmlType="submit" block loading={loading} icon={<ArrowRightOutlined />} style={{ height: '50px', borderRadius: '10px', fontSize: '1rem', fontWeight: '700', background: 'linear-gradient(135deg,#AD1457,#D81B60)', border: 'none', boxShadow: '0 6px 20px rgba(173,20,87,0.35)' }}>
                                    Reset Password
                                </Button>
                            </Form.Item>
                        </Form>
                    )}

                    <Divider style={{ margin: '20px 0', borderColor: '#E2E8F0' }}>
                        <Text style={{ color: '#94A3B8', fontSize: '0.82rem' }}>OR</Text>
                    </Divider>

                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                        <Text style={{ color: '#64748B', fontSize: '0.9rem' }}>Remember your password?{' '}</Text>
                        <Link to="/login" style={{ color: '#AD1457', fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none' }}>Sign In</Link>
                    </div>

                    <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid #F1F5F9' }}>
                        <Text style={{ color: '#94A3B8', fontSize: '0.78rem' }}>
                            {process.env.REACT_APP_RELEASE_DATE} · v{process.env.REACT_APP_RELEASE_VERSION}
                        </Text>
                    </div>
                </div>
            </AuthLayout>
        </>
    );
};

export default ResetPassword;
